import { MessageCircleMore } from "lucide-react";
import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Tooltip } from "@/components/ui/misc";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/components/ui/toast";
import { closeQuickChat, openQuickChat, QUICK_CHAT_CONTENT, QuickChat, useQuickChat } from "@/features/quick-chat";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { arrivals, previewText, replyArrivals, unreadTotal } from "@/lib/chats";
import { railOffset } from "@/lib/phone-page";
import { useChats, useCloserAskAction } from "@/lib/queries";
import type { ChatSummary } from "@/lib/types";
import { useMediaQuery } from "@/lib/use-media-query";
import { cn } from "@/lib/utils";
import { RAIL_CELL } from "@/softphone/rail";
import { PHONE_COMPANION, PHONE_DOCKED } from "@/softphone/voicemail-suggestion";

/**
 * Team Chat outside its page (platform ADR-186): the count in the sidebar and the phone's
 * rail, and a toast for a message that arrives while the person is anywhere else. All three read
 * the one list of chats, which the person's `chats` hint refetches.
 *
 * A core-api older than messages answers `/chats` with 404: no count, no toast, no retries.
 */

function useChatList() {
  return useChats({
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 2,
  });
}

/** Unread messages across the person's chats; null where this server has no messages. */
export function useUnreadMessages(): number | null {
  const chats = useChatList();
  if (chats.error instanceof ApiError && chats.error.status === 404) return null;
  return unreadTotal(chats.data);
}

/** Renders nothing: a toast for each message from somebody else that arrives in a chat the person
 *  does not have open, with a button to open it. An ask for a closer the person may take
 *  (platform ADR-187) says so, and its button takes it: the first press wins, so the toast is the
 *  quickest place to press. */
export function MessagesWatcher() {
  const { user } = useAuth();
  const chats = useChatList();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const take = useCloserAskAction().mutate;
  const before = React.useRef<ChatSummary[] | undefined>(undefined);
  const quick = useQuickChat();
  const docked = useMediaQuery(PHONE_DOCKED);
  const match = /^\/messages\/(\d+)/.exec(location.pathname);
  // The conversation on screen: the Team Chat page's, else the one open in the rail's window; and
  // the thread on screen in it (platform ADR-191).
  const openId = match ? Number(match[1]) : quick.open ? quick.chatId : null;
  const openThread = match
    ? Number(new URLSearchParams(location.search).get("thread")) || null
    : quick.open
      ? quick.threadId
      : null;

  React.useEffect(() => {
    if (!chats.data || !user) return;
    // One ask is a card in two chats, the team's and the contact's (platform ADR-189): one toast.
    const told = new Set<number>();
    for (const chat of arrivals(before.current, chats.data, user.id, openId)) {
      const who = chat.last_message?.sender_name ?? "Somebody";
      const ask = chat.last_message?.handoff;
      if (ask) {
        if (told.has(ask.id)) continue;
        told.add(ask.id);
      }
      if (ask && ask.state === "waiting" && ask.can_take) {
        toast.notify(`${who} needs a closer`, ask.note ?? `In ${chat.title}`, {
          label: "Take call",
          onSelect: () =>
            take(
              { id: ask.id, action: "take" },
              {
                onSuccess: () =>
                  toast.success("You took it", `${who} transfers the caller to you next.`),
                onError: (e) =>
                  toast.error(
                    "Could not take it",
                    e instanceof ApiError ? e.message : "Try again from Team Chat.",
                  ),
              },
            ),
        });
        continue;
      }
      const where = chat.kind === "contact" ? `the conversation about ${chat.title}` : chat.title;
      // A mention (platform ADR-186) is in the bell too, which leaves the toast to this one.
      const title = chat.last_message?.mentions_me
        ? `${who} mentioned you in ${where}`
        : chat.kind === "team"
          ? `${who} in ${chat.title}`
          : chat.kind === "contact"
            ? `${who} about ${chat.title}`
            : who;
      toast.notify(title, previewText(chat.last_message, user.id), {
        label: "Open",
        // Beside the rail it opens in the window, so a rep on a call stays where they are.
        onSelect: () => (docked ? openQuickChat(chat.id) : navigate(`/messages/${chat.id}`)),
      });
    }
    // A reply in a thread the person follows (platform ADR-191).
    for (const chat of replyArrivals(before.current, chats.data, user.id, openThread)) {
      const reply = chat.last_reply;
      if (!reply) continue;
      const who = reply.sender_name ?? "Somebody";
      const where = chat.kind === "contact" ? `the conversation about ${chat.title}` : chat.title;
      toast.notify(
        reply.mentions_me ? `${who} mentioned you in a thread in ${where}` : `${who} replied in a thread in ${where}`,
        reply.preview,
        {
          label: "Open",
          onSelect: () =>
            docked
              ? openQuickChat(chat.id, reply.thread_id)
              : navigate(`/messages/${chat.id}?thread=${reply.thread_id}`),
        },
      );
    }
    before.current = chats.data;
  }, [chats.data, user, openId, openThread, toast, navigate, take, docked]);
  return null;
}

/**
 * The Team Chat button in the phone's rail, under the bell: the unread count, and a small chat window
 * beside the rail (`features/quick-chat.tsx`), so a rep talks to their team or a colleague without
 * leaving the page they are on. One button, however many conversations the person is in (ACL-607):
 * it opens the window on the list of them all. Built as the bell is: its own popover root and the
 * companion mark, out beside what else the rail has open (`railOffset`), and out of the way by
 * itself when a call rings.
 */
export function MessagesRailButton({
  phoneOpen = false, suggestionOpen = false, ringing = false,
}: {
  /** The phone's own popover is open. */
  phoneOpen?: boolean;
  /** A voicemail is suggested and its choices are open. */
  suggestionOpen?: boolean;
  /** A call is ringing this phone. */
  ringing?: boolean;
}) {
  const chats = useChatList();
  const unread = useUnreadMessages();
  const quick = useQuickChat();
  const location = useLocation();
  const [tipOpen, setTipOpen] = React.useState(false);
  React.useEffect(() => {
    if (ringing || suggestionOpen) closeQuickChat();
  }, [ringing, suggestionOpen]);
  if (unread === null) return null;
  const here = quick.open || location.pathname.startsWith("/messages");
  const name = unread > 0 ? `Team Chat, ${unread} unread` : "Team Chat";
  const companion = { [PHONE_COMPANION]: "" };
  return (
    <Popover open={quick.open} onOpenChange={(next) => (next ? openQuickChat(null) : closeQuickChat())}>
      <PopoverAnchor asChild>
        <div className={RAIL_CELL} {...companion}>
          <Tooltip content={name} side="left" open={tipOpen && !quick.open} onOpenChange={setTipOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                // Radix opens the tooltip from a focus handler that stands down for a prevented event.
                onFocus={(e) => e.preventDefault()}
                aria-label={name}
                className={cn(
                  "relative flex size-9 items-center justify-center rounded-full transition-colors",
                  here ? "bg-frame-hover text-text" : "text-text-muted hover:bg-frame-hover hover:text-text",
                )}
              >
                <MessageCircleMore className="size-[18px]" aria-hidden />
                {unread > 0 && (
                  <span
                    className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger-9 px-1 text-2xs font-semibold leading-none text-white ring-2 ring-frame tabular"
                    aria-hidden
                  >
                    {unread > 99 ? "99+" : unread}
                  </span>
                )}
              </button>
            </PopoverTrigger>
          </Tooltip>
        </div>
      </PopoverAnchor>
      <PopoverContent
        side="left"
        align="start"
        sideOffset={railOffset({ phoneOpen, suggestionOpen })}
        aria-label="Team Chat"
        className={QUICK_CHAT_CONTENT}
        // Escape in the composer while its list of people to mention is open closes the list.
        onEscapeKeyDown={(e) => {
          const target = e.target as HTMLElement | null;
          if (target?.closest?.("[data-mention-picking]")) e.preventDefault();
        }}
        {...companion}
      >
        <QuickChat chats={chats.data ?? []} />
      </PopoverContent>
    </Popover>
  );
}
